import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IItemForGetShortInfo, UserRoles, IGetRestaurantsShort, IGetRestaurantsShortArgs } from 'types';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { MultipleSelect } from './MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './index.module.scss';

interface IProps {
	setIDsRestaurant: ({ value, isInitialSet }: { value: string[]; isInitialSet: boolean }) => void;
	setFetchedRestaurants: (value: IItemForGetShortInfo[]) => void;
	changeLoaderStatus: (value: boolean) => void;
	IDSelectedCustomer: Nullable<string>;
	initialIDsSelectedRestaurant: string[];
	IDsSelectedRestaurant: string[];
}

export const RestaurantsMultipleSelect: React.FC<IProps> = React.memo(
	({
		setIDsRestaurant,
		setFetchedRestaurants,
		changeLoaderStatus,
		IDSelectedCustomer,
		initialIDsSelectedRestaurant,
		IDsSelectedRestaurant,
	}) => {
		const { loggedUser } = useContext<IAppContext>(AppContext);
		const hasAccess = useRoleAccess([
			UserRoles.SUPER_USER,
			UserRoles.CLIENT_MANAGER,
			UserRoles.GROUP_MANAGER,
			UserRoles.TECHNICAL,
		]);
		const { response, loading, refetch, responseError } = useFetchData<
			IGetRestaurantsShort,
			IGetRestaurantsShortArgs
		>(IDSelectedCustomer ? getRestaurantsShortInfo : null, {
			customerId: IDSelectedCustomer || '',
			menuApiVersion: 'v2',
		});
		const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

		useEffect(() => {
			refetch();
		}, [IDSelectedCustomer]);

		const handleRemove = useCallback(
			(id: string) => {
				const updatedSelectedItems = IDsSelectedRestaurant.filter((el) => el !== id);
				setIDsRestaurant({ value: updatedSelectedItems, isInitialSet: false });
			},
			[setIDsRestaurant, IDsSelectedRestaurant],
		);

		const restaurantsList = useMemo(() => {
			return IDsSelectedRestaurant.map((el) => {
				const label = response?.data.restaurants.find((item) => item.id === el)?.name || '';
				const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
				return (
					<SelectedItem
						key={el}
						label={label}
						onDelete={
							!hasAccess && !accessibleRestaurantIds?.includes(el) ? undefined : () => handleRemove(el)
						}
					/>
				);
			});
		}, [IDsSelectedRestaurant, response, loggedUser, hasAccess, handleRemove]);

		useEffect(() => {
			if (response) {
				const accessibleRestaurants = loggedUser.restaurants ? loggedUser.restaurants : [];
				const restaurants = hasAccess ? response.data.restaurants : accessibleRestaurants;
				setFetchedRestaurants(restaurants);
			}
			if (isFirstRender && response) {
				setIDsRestaurant({ value: initialIDsSelectedRestaurant, isInitialSet: true });
				changeLoaderStatus(false);
				setIsFirstRender(false);
			}
		}, [response, loggedUser, hasAccess, initialIDsSelectedRestaurant]);

		const handleSetIDsRestaurant = useCallback(
			(value: string[]) => {
				setIDsRestaurant({ value, isInitialSet: false });
			},
			[setIDsRestaurant],
		);

		return (
			<div className={style.container} data-test="RestaurantsMultipleSelect">
				<MultipleSelect
					data={response?.data.restaurants || []}
					isLoading={loading || isFirstRender}
					refetch={refetch}
					responseError={responseError}
					setIDsRestaurant={handleSetIDsRestaurant}
					IDSelectedCustomer={IDSelectedCustomer}
					IDsSelectedRestaurant={IDsSelectedRestaurant}
				/>
				<div className={style.groupsList}>{restaurantsList}</div>
			</div>
		);
	},
);
